import React, { useState, useEffect } from 'react';
import { Table, Input, Button, message } from 'antd';
import {
    useGetBudgetCategoriesQuery,
    useUpdateBudgetMutation,
    useGetBudgetsQuery
} from '../../redux/api/budgetTracker';
import { useSelector } from 'react-redux';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

const BudgetTrackerSettingsPage = () => {
    const selectedClient = useSelector((state) => state.genericReporting.selectedDropdownValues['client_name']);
    const selectedRegion = useSelector((state) => state.genericReporting.selectedDropdownValues['single_select_client_region']);
    const selectedStartDate = useSelector((state) => state.genericReporting.selectedDropdownValues['start_date']);
    const selectedEndDate = useSelector((state) => state.genericReporting.selectedDropdownValues['end_date']);
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [selectedMonthStr, setSelectedMonthStr] = useState(dayjs().format('YYYY-MM'));
    
    const budgetCategories = useGetBudgetCategoriesQuery({
        client: selectedClient,
        client_region: selectedRegion,
        startDate: selectedStartDate,
        endDate: selectedEndDate,
    });

    const budgets = useGetBudgetsQuery({
        client: selectedClient,
        client_region: selectedRegion,
        month: selectedMonthStr,
    }, { refetchOnMountOrArgChange: true });

    useEffect(() => {
        // Force refetch when the month string changes
        if (selectedMonthStr && budgets && typeof budgets.refetch === 'function') {
            budgets.refetch();
        }
    }, [selectedMonthStr]);

    const [updateBudget] = useUpdateBudgetMutation();
    const [dataSource, setDataSource] = useState([]);
    const [editedBudgets, setEditedBudgets] = useState({});
    const [editedRevenueTargets, setEditedRevenueTargets] = useState({});

    useEffect(() => {
        if (budgetCategories.data && budgets.data) {
            const tableData = budgetCategories.data.map((category) => {
                const entry = budgets.data[category];
                let initialBudget = 0;
                let initialRevenueTarget = 0;
                if (entry !== undefined && entry !== null) {
                    if (typeof entry === 'object') {
                        if (typeof entry.budget === 'number') initialBudget = entry.budget;
                        if (typeof entry.revenue_target === 'number') initialRevenueTarget = entry.revenue_target;
                    } else if (typeof entry === 'number') {
                        initialBudget = entry;
                    }
                }
                return {
                    key: category,
                    channel: category,
                    budget: initialBudget,
                    revenue_target: initialRevenueTarget,
                    deleted: initialBudget === -1,
                };
            });
            setDataSource(tableData);
            setEditedBudgets({});
            setEditedRevenueTargets({});
        }
    }, [budgetCategories.data, budgets.data]);

    const handleInputChange = (record, value) => {
        const nextValue = value === '' ? '' : parseFloat(value);
        setEditedBudgets((prev) => ({
            ...prev,
            [record.channel]: nextValue,
        }));
    };

    const handleDelete = (record) => {
        setDataSource((prev) =>
            prev.map((item) =>
                item.key === record.key
                    ? { ...item, budget: -1, deleted: true }
                    : item
            )
        );

        setEditedBudgets((prev) => ({
            ...prev,
            [record.channel]: -1,
        }));
    };

    const handleRestore = (record) => {
        setDataSource((prev) =>
            prev.map((item) =>
                item.key === record.key
                    ? { ...item, budget: 0, deleted: false }
                    : item
            )
        );

        setEditedBudgets((prev) => ({
            ...prev,
            [record.channel]: 0,
        }));
    };

    const handleRevenueTargetChange = (record, value) => {
        const nextValue = value === '' ? '' : parseFloat(value);
        setEditedRevenueTargets((prev) => ({
            ...prev,
            [record.channel]: nextValue,
        }));
    };

    const handleSubmit = async () => {
        const channelsWithBudgetUpdates = Object.keys(editedBudgets);
        const channelsWithRevenueUpdates = Object.keys(editedRevenueTargets);
        const channelsToUpdate = Array.from(new Set([...channelsWithBudgetUpdates, ...channelsWithRevenueUpdates]));

        try {
            for (const channel of channelsToUpdate) {
                const budget = editedBudgets[channel];
                const revenueTarget = editedRevenueTargets[channel];
                if (budget !== undefined && isNaN(budget)) {
                    throw new Error(`Invalid budget for channel: ${channel}`);
                }
                if (revenueTarget !== undefined && isNaN(revenueTarget)) {
                    throw new Error(`Invalid revenue target for channel: ${channel}`);
                }

                const budgetValue = budget === '' ? undefined : budget;
                const revenueTargetValue = revenueTarget === '' ? undefined : revenueTarget;
                await updateBudget({
                    client: selectedClient,
                    client_region: selectedRegion,
                    month: selectedMonthStr,
                    channel,
                    ...(budgetValue !== undefined ? { budget: budgetValue } : {}),
                    ...(revenueTargetValue !== undefined ? { revenue_target: revenueTargetValue } : {}),
                }).unwrap();
            }

            message.success('Budgets updated successfully.');
            setEditedBudgets({});
            setEditedRevenueTargets({});
        } catch (error) {
            message.error(`Failed to update budgets: ${error.message}`);
        }
    };

    const columns = [
        {
            title: 'Channel',
            dataIndex: 'channel',
            key: 'channel',
            render: (text, record) =>
                record.deleted ? <del>{text}</del> : text,
        },
        {
            title: 'Spend Target',
            dataIndex: 'budget',
            key: 'budget',
            render: (text, record) => {
                if (record.deleted) return <span>Deleted</span>;
                const edited = editedBudgets[record.channel];
                const value = edited === '' ? '' : (edited !== undefined ? edited : text);
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => handleInputChange(record, e.target.value)}
                    />
                );
            },
        },
        {
            title: 'Revenue Target',
            dataIndex: 'revenue_target',
            key: 'revenue_target',
            render: (text, record) => {
                if (record.deleted) return <span>Deleted</span>;
                const edited = editedRevenueTargets[record.channel];
                const value = edited === '' ? '' : (edited !== undefined ? edited : (record.revenue_target || 0));
                return (
                    <Input
                        type="number"
                        value={value}
                        onChange={(e) => handleRevenueTargetChange(record, e.target.value)}
                    />
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) =>
                record.deleted ? (
                    <Button type="link" onClick={() => handleRestore(record)}>
                        Restore
                    </Button>
                ) : (
                    <Button type="link" danger onClick={() => handleDelete(record)}>
                        Delete
                    </Button>
                ),
        },
    ];

    return (
        <div className='flex-column' style={{
            'alignItems': 'center',
            'justifyContent': 'center',
            'marginTop': '5vh',
        }}>
            <h1>Target Settings</h1>
            <DatePicker
                picker="month"
                format={'YYYY-MM'}
                value={selectedMonth}
                onChange={(date) => {
                    setSelectedMonth(date);
                    setSelectedMonthStr(date ? date.format('YYYY-MM') : undefined);
                }}
            />
            <br></br>
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={
                    (budgetCategories.isLoading || budgets.isLoading ||
                    budgetCategories.isFetching || budgets.isFetching)
                }
                rowClassName={(record) => (record.deleted ? 'deleted-row' : '')}
                pagination={false}
                style={{
                    'width': '50vw',
                    'margin': 'auto',
                }}
            />
            <Button type="primary" onClick={handleSubmit} style={{
                marginTop: '16px',
                marginBottom: '10vh',
                width: '10vw',
                alignSelf: 'center',
            }}>
                Submit
            </Button>
        </div>
    );
};

export default BudgetTrackerSettingsPage;
